import { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import { ClarityOneSessionRecovery } from "@/components/clarity-one/ClarityOneSessionRecovery";
import { CLARITY_ONE_ASSISTANCE } from "@shared/clarityOneContact";
import { formatClarityOnePhone } from "@/lib/clarityOneFormat";
import { getClarityOnePageOccurrenceId } from "@/lib/clarityOnePageOccurrence";
import { useClarityOneMeSession } from "@/lib/useClarityOneMeSession";
import {
  staffCustomerCardReturnUrl,
  unauthenticatedLoginPath,
} from "@/lib/clarityOneSessionGate";
import {
  ChevronRight,
  FileText,
  FolderOpen,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Receipt,
  ScrollText,
  Package,
  Shield,
  User,
  Wallet,
} from "lucide-react";

function formatSgd(amount: number | null | undefined): string {
  const n = typeof amount === "number" ? amount : Number(amount ?? 0);
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
  }).format(Number.isFinite(n) ? n : 0);
}

function formatUsdBalance(amount: string | number | null | undefined): string {
  const n = typeof amount === "number" ? amount : parseFloat(String(amount ?? "0"));
  return `US$${Number.isFinite(n) ? n.toFixed(2) : "0.00"}`;
}

function AssistanceRow({
  icon: Icon,
  label,
  href,
}: {
  icon: typeof Phone;
  label: string;
  href: string;
}) {
  return (
    <a
      href={href}
      target={href.startsWith("http") ? "_blank" : undefined}
      rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
      className="flex items-center gap-4 bg-[#FAFAF7] rounded-xl border border-[#E8E4DC] px-5 py-4 no-underline transition-all hover:border-[#B8956E] group"
    >
      <div className="h-10 w-10 rounded-lg bg-white border border-[#E8E4DC] flex items-center justify-center shrink-0">
        <Icon className="h-4 w-4 text-[#8B7355]" strokeWidth={1.5} />
      </div>
      <span className="flex-1 text-sm font-medium text-[#1A2744]">{label}</span>
      <ChevronRight className="h-4 w-4 text-[#B8956E] shrink-0 opacity-70 group-hover:opacity-100" />
    </a>
  );
}

function ModuleCard({
  href,
  eyebrow,
  title,
  summaryLines,
  icon: Icon,
}: {
  href: string;
  eyebrow: string;
  title: string;
  summaryLines: string[];
  icon: typeof FileText;
}) {
  return (
    <Link
      href={href}
      className="block min-h-[7.5rem] min-w-0 no-underline group h-full sm:min-h-0"
      data-testid="clarity-one-home-module-card"
    >
      <div className="flex h-full min-w-0 flex-col rounded-2xl border border-[#E8E4DC] bg-white p-4 transition-all group-hover:border-[#B8956E] group-hover:shadow-sm sm:p-6 lg:p-8">
        <div className="mb-3 flex items-start justify-between gap-2 sm:mb-6 sm:gap-4">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#E8E4DC] bg-[#FAFAF7] sm:h-12 sm:w-12">
            <Icon className="h-4 w-4 text-[#8B7355] sm:h-5 sm:w-5" strokeWidth={1.5} />
          </div>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[#B8956E]/80 sm:mt-1 sm:h-5 sm:w-5" />
        </div>
        <p className="mb-1 text-[10px] uppercase tracking-[0.18em] text-[#B8956E] sm:mb-2 sm:text-[11px] sm:tracking-[0.2em]">
          {eyebrow}
        </p>
        <p className="mb-2 break-words text-base font-medium leading-snug text-[#1A2744] sm:mb-4 sm:text-xl">
          {title}
        </p>
        <div className="mt-auto min-w-0 space-y-0.5 sm:space-y-1">
          {summaryLines.map((line) => (
            <p key={line} className="break-words text-xs leading-snug text-[#8B7355] sm:text-sm">
              {line}
            </p>
          ))}
        </div>
      </div>
    </Link>
  );
}

function ActionCentreCard({
  headline,
  href,
  ctaLabel,
}: {
  headline: string;
  href: string;
  ctaLabel: string;
}) {
  return (
    <div
      className="mb-8 lg:mb-10 rounded-2xl bg-[#1A2744] text-white px-6 py-5 sm:px-8 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-5"
      data-testid="clarity-one-action-centre"
    >
      <div className="h-12 w-12 rounded-full border border-[#B8956E]/50 bg-[#1A2744] flex items-center justify-center shrink-0">
        <FileText className="h-5 w-5 text-[#B8956E]" strokeWidth={1.5} />
      </div>
      <p className="flex-1 text-sm sm:text-base leading-relaxed text-white/95">{headline}</p>
      <Link
        href={href}
        className="inline-flex items-center justify-center gap-1.5 shrink-0 rounded-lg bg-white text-[#1A2744] text-[11px] tracking-[0.12em] uppercase font-medium px-5 py-3 no-underline hover:bg-[#FAFAF7] transition-colors"
      >
        {ctaLabel}
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
      </Link>
    </div>
  );
}

export default function ClarityOneHomePage() {
  const [, setLocation] = useLocation();
  const recordPageView = trpc.clarityOneAuth.recordPageView.useMutation({
    meta: { background: true },
  });
  const { meQuery, sessionView, retry } = useClarityOneMeSession();
  const me = meQuery.data;

  const { data: summary } = trpc.clarityOneRecords.homeSummary.useQuery(undefined, {
    enabled: sessionView.kind === "ready",
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const walletEnabled = Boolean(me?.kaleidescapeWalletEnabled);
  const {
    data: walletData,
    isLoading: walletLoading,
    isError: walletError,
  } = trpc.kaleidescapeWallet.customerGetWallet.useQuery(undefined, {
    enabled: !!me && walletEnabled,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (sessionView.kind === "unauthenticated") {
      setLocation(unauthenticatedLoginPath());
    }
  }, [sessionView.kind, setLocation]);

  useEffect(() => {
    if (sessionView.kind !== "ready" || !me) return;
    recordPageView.mutate({
      page: "home",
      occurrenceId: getClarityOnePageOccurrenceId("home"),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id, sessionView.kind]);

  if (sessionView.kind === "loading") {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24" data-testid="clarity-one-home-loading">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  if (sessionView.kind === "recovery") {
    const returnUrl = staffCustomerCardReturnUrl(typeof document !== "undefined" ? document.referrer : null);
    return (
      <ClarityOneLayout showNav={false}>
        <ClarityOneSessionRecovery
          reason={sessionView.reason}
          onRetry={retry}
          returnUrl={returnUrl}
          testId="clarity-one-home-recovery"
        />
      </ClarityOneLayout>
    );
  }

  if (sessionView.kind === "unauthenticated" || !me) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  const phoneDisplay = formatClarityOnePhone(me.contact);
  const emailDisplay = me.email?.trim() || null;
  const attention = summary?.attention;
  const showActionCentre = Boolean(attention?.headline && attention.primaryHref);

  const projectsSummary = summary
    ? [
        summary.activeProjectCount === 1
          ? "1 active"
          : `${summary.activeProjectCount} active`,
        summary.pendingApprovalCount === 1
          ? "1 pending approval"
          : `${summary.pendingApprovalCount} pending approval`,
        summary.totalProjectCount === 1
          ? "1 total"
          : `${summary.totalProjectCount} total`,
      ]
    : ["View your projects"];

  const documentSummary = summary
    ? [
        summary.projectCount === 1 ? "1 project" : `${summary.projectCount} projects`,
        summary.documentCount === 1 ? "1 document" : `${summary.documentCount} documents`,
      ]
    : ["View project documents"];

  const invoiceSummary = summary
    ? [
        summary.invoiceOutstandingCount === 1
          ? "1 outstanding"
          : `${summary.invoiceOutstandingCount} outstanding`,
        summary.invoiceTotalCount === 1
          ? "1 total invoice"
          : `${summary.invoiceTotalCount} total invoices`,
      ]
    : ["View your invoices"];

  const statementSummary = summary
    ? [
        `Outstanding Balance ${formatSgd(summary.statementOutstandingBalance)}`,
        `Due Today ${formatSgd(summary.statementDueToday)}`,
      ]
    : ["View your account statement"];

  const warrantySummary = summary
    ? [
        summary.activeWarrantyItemCount === 1
          ? "1 active warranty"
          : `${summary.activeWarrantyItemCount} active warranties`,
        ...(summary.expiringSoonWarrantyItemCount > 0
          ? [
              summary.expiringSoonWarrantyItemCount === 1
                ? "1 expiring soon"
                : `${summary.expiringSoonWarrantyItemCount} expiring soon`,
            ]
          : []),
        summary.issuedWarrantyItemCount === 1
          ? "1 total issued"
          : `${summary.issuedWarrantyItemCount} total issued`,
      ]
    : ["View your warranty coverage"];

  const walletSecondaryLine = "Manage your wallet and top-ups";
  const walletSummaryLines = (() => {
    if (walletLoading) {
      return [walletSecondaryLine];
    }
    if (walletError) {
      return [walletSecondaryLine];
    }
    if (!walletData?.wallet) {
      return ["Wallet setup in progress"];
    }
    return [formatUsdBalance(walletData.wallet.currentBalance), walletSecondaryLine];
  })();

  const actionCta =
    attention?.actions.length === 1
      ? attention.actions[0]!.kind === "proposal_pending"
        ? "Review Proposal"
        : attention.actions[0]!.kind === "vo_pending"
          ? "Review Variation Order"
          : "View Invoices"
      : "Review Actions";

  const actionHref =
    attention?.actions.length === 1
      ? attention.primaryHref!
      : attention?.actions.some((a) => a.kind === "proposal_pending" || a.kind === "vo_pending")
        ? "/proposals?tab=pending_approval"
        : attention?.primaryHref ?? "/proposals";

  return (
    <ClarityOneLayout showNav>
      <div className="mb-8 lg:mb-10">
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E] mb-2">Welcome back</p>
        <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-light text-[#1A2744] tracking-tight leading-tight mb-3">
          {me.name}
        </h1>
        <p className="text-sm sm:text-base text-[#8B7355] max-w-xl leading-relaxed">
          Thank you for being part of the Clarity experience.
        </p>
      </div>

      {showActionCentre ? (
        <ActionCentreCard
          headline={attention!.headline!}
          href={actionHref}
          ctaLabel={actionCta}
        />
      ) : null}

      <div className="mb-10 lg:mb-12">
        <h2 className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#B8956E] mb-4">
          Need Assistance?
        </h2>
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-3"
          data-testid="clarity-one-home-assistance-grid"
        >
          <AssistanceRow
            icon={MessageCircle}
            label={CLARITY_ONE_ASSISTANCE.whatsAppLabel}
            href={CLARITY_ONE_ASSISTANCE.whatsAppUrl}
          />
          <AssistanceRow
            icon={Phone}
            label={CLARITY_ONE_ASSISTANCE.phoneDisplay}
            href={CLARITY_ONE_ASSISTANCE.phoneHref}
          />
          <AssistanceRow
            icon={Mail}
            label={CLARITY_ONE_ASSISTANCE.emailLabel}
            href={CLARITY_ONE_ASSISTANCE.emailHref}
          />
        </div>
      </div>

      <div
        className="mb-10 grid grid-cols-2 gap-3 sm:gap-6 lg:mb-12 lg:grid-cols-3 lg:gap-8"
        data-testid="clarity-one-home-module-grid"
      >
        {summary?.totalProjectCount !== 0 ? (
          <ModuleCard
            href="/proposals"
            eyebrow="Commercial"
            title="My Projects"
            summaryLines={projectsSummary}
            icon={FileText}
          />
        ) : null}
        {summary?.documentCount !== 0 ? (
          <ModuleCard
            href="/documents"
            eyebrow="Technical"
            title="Project Documents"
            summaryLines={documentSummary}
            icon={FolderOpen}
          />
        ) : null}
        {summary?.invoiceTotalCount !== 0 ? (
          <ModuleCard
            href="/invoices"
            eyebrow="Billing"
            title="My Invoices"
            summaryLines={invoiceSummary}
            icon={Receipt}
          />
        ) : null}
        {summary?.issuedWarrantyItemCount !== 0 ? (
          <ModuleCard
            href="/warranty"
            eyebrow="Coverage"
            title="Warranty"
            summaryLines={warrantySummary}
            icon={Shield}
          />
        ) : null}
        {Number(summary?.deliveredItemCount ?? 0) > 0 ? (
          <ModuleCard
            href="/deliveries"
            eyebrow="Delivery"
            title="Deliveries"
            summaryLines={["Current equipment at your sites"]}
            icon={Package}
          />
        ) : null}
        {summary?.hasFinancialHistory !== false ? (
          <ModuleCard
            href="/account/statement"
            eyebrow="Finance"
            title="Account Statement"
            summaryLines={statementSummary}
            icon={ScrollText}
          />
        ) : null}
        {walletEnabled ? (
          <ModuleCard
            href="/wallet/kaleidescape"
            eyebrow="Entertainment"
            title="Kaleidescape Wallet"
            summaryLines={walletSummaryLines}
            icon={Wallet}
          />
        ) : null}
      </div>

      <section>
        <h2 className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#B8956E] mb-4">
          My Account
        </h2>
        <Link href="/account" className="block no-underline group">
          <div className="bg-white rounded-2xl border border-[#E8E4DC] px-6 py-5 flex items-center gap-5 transition-all group-hover:border-[#B8956E] group-hover:shadow-sm">
            <div className="h-14 w-14 rounded-full bg-[#F0EDE6] border border-[#E8E4DC] flex items-center justify-center shrink-0">
              <User className="h-6 w-6 text-[#8B7355]" strokeWidth={1.25} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] tracking-[0.15em] uppercase text-[#B8956E] mb-1">My Account</p>
              <p className="text-lg font-medium text-[#1A2744] truncate">{me.name}</p>
              <p className="text-sm text-[#8B7355] mt-1 truncate">
                {[phoneDisplay !== "—" ? phoneDisplay : null, emailDisplay].filter(Boolean).join("  ·  ") ||
                  "Tap to update your contact details"}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-[#B8956E] shrink-0 opacity-60 group-hover:opacity-100" />
          </div>
        </Link>
      </section>
    </ClarityOneLayout>
  );
}
